import * as THREE from 'three';
import type { Run } from '../sim/run';
import type { PickupKind, PickupSpawn } from '../sim/terrain/profile';
import { InstancedModel, instantiate, type GameModels } from './assets';
import { softDotTexture } from './particles';

const CAPACITY: Record<PickupKind, number> = { fuel: 16, coin: 96 };
const VIEW_BEHIND = 25;
const VIEW_AHEAD = 60;
/** The gas can model stands on Y=0; centre it on the pickup point. */
const GAS_CAN_CENTRE_Y = 0.35;
const GAS_CAN_SCALE = 1.4;
const COIN_SCALE = 1.3;
/** Soft gold glow behind each coin so it stays readable even edge-on mid-spin. */
const HALO_SIZE = 1.25;

interface Pop {
  group: THREE.Group;
  materials: THREE.Material[];
  age: number;
  startY: number;
}

/**
 * Pickups near the camera: spinning coins and bobbing gas cans (Kiln GLBs, instanced).
 * Shape and motion separate them, not only colour. Collected pickups pop and fade.
 */
export class PickupsView {
  readonly group = new THREE.Group();
  private readonly coins: InstancedModel;
  private readonly fuels: InstancedModel;
  private readonly halos: THREE.InstancedMesh;
  private readonly haloScale = new THREE.Vector3();
  private readonly identity = new THREE.Quaternion();
  private readonly pops: Pop[] = [];
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly pos = new THREE.Vector3();
  private readonly scale = new THREE.Vector3();
  private readonly axisY = new THREE.Vector3(0, 1, 0);
  reducedMotion = false;

  constructor(private readonly models: GameModels) {
    this.coins = new InstancedModel(models.coin, CAPACITY.coin, this.group);
    this.fuels = new InstancedModel(models.gasCan, CAPACITY.fuel, this.group);
    this.halos = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: softDotTexture(), color: 0xffc94d, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, fog: false }),
      CAPACITY.coin,
    );
    this.halos.frustumCulled = false;
    this.halos.count = 0;
    this.group.add(this.halos);
  }

  update(run: Run, cameraX: number, time: number, dt: number): void {
    const t = this.reducedMotion ? 0 : time;
    let fuelCount = 0;
    let coinCount = 0;
    for (const p of run.terrain.pickupsIn(cameraX - VIEW_BEHIND, cameraX + VIEW_AHEAD)) {
      if (run.isCollected(p.id)) continue;
      if (p.kind === 'fuel' && fuelCount < CAPACITY.fuel) this.writeFuel(fuelCount++, p, t);
      else if (p.kind === 'coin' && coinCount < CAPACITY.coin) this.writeCoin(coinCount++, p, t);
    }
    this.fuels.commit(fuelCount);
    this.coins.commit(coinCount);
    this.halos.count = coinCount;
    this.halos.instanceMatrix.needsUpdate = true;
    this.updatePops(dt);
  }

  /** Spawn a pop at a collected pickup. */
  pop(kind: PickupKind, x: number, y: number): void {
    const group = instantiate(kind === 'coin' ? this.models.coin : this.models.gasCan, false);
    const materials: THREE.Material[] = [];
    group.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const material = (mesh.material as THREE.Material).clone();
      material.transparent = true;
      mesh.material = material;
      materials.push(material);
    });
    group.position.set(x, y - (kind === 'fuel' ? GAS_CAN_CENTRE_Y : 0), 0);
    this.group.add(group);
    this.pops.push({ group, materials, age: 0, startY: group.position.y });
  }

  clearPops(): void {
    for (const p of this.pops) this.disposePop(p);
    this.pops.length = 0;
  }

  private writeFuel(i: number, p: PickupSpawn, t: number): void {
    // Gentle bob and a small sway, so the can's face stays toward the camera.
    this.quat.setFromAxisAngle(this.axisY, Math.sin(t * 1.6 + p.id) * 0.35);
    this.pos.set(p.x, p.y - GAS_CAN_CENTRE_Y * GAS_CAN_SCALE + Math.sin(t * 2 + p.id) * 0.08, 0);
    this.scale.setScalar(GAS_CAN_SCALE);
    this.fuels.set(i, this.matrix.compose(this.pos, this.quat, this.scale));
  }

  private writeCoin(i: number, p: PickupSpawn, t: number): void {
    this.quat.setFromAxisAngle(this.axisY, t * 3 + p.id);
    this.pos.set(p.x, p.y, 0);
    this.scale.setScalar(COIN_SCALE);
    this.coins.set(i, this.matrix.compose(this.pos, this.quat, this.scale));
    const pulse = 1 + Math.sin(t * 4 + p.id) * 0.08;
    this.pos.z = -0.15;
    this.haloScale.setScalar(HALO_SIZE * pulse);
    this.halos.setMatrixAt(i, this.matrix.compose(this.pos, this.identity, this.haloScale));
  }

  private updatePops(dt: number): void {
    for (let i = this.pops.length - 1; i >= 0; i--) {
      const p = this.pops[i];
      p.age += dt;
      const k = Math.min(1, p.age / 0.3);
      p.group.scale.setScalar(1.6 - 0.6 * k);
      p.group.position.y = p.startY + k * 1.2;
      for (const m of p.materials) m.opacity = 1 - k;
      if (k >= 1) {
        this.disposePop(p);
        this.pops.splice(i, 1);
      }
    }
  }

  private disposePop(p: Pop): void {
    this.group.remove(p.group);
    for (const m of p.materials) m.dispose();
  }
}
